"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CodeXml, Layers, Globe as Globe2, Sparkles, ArrowUpRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface SpecialtyItem {
  id: string;
  tag: string;
  title: string;
  description: string;
  deliverables: string[];
  linkTo: string;
  linkText: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const specialtiesData: SpecialtyItem[] = [{
  id: "technical-saas",
  tag: "Software & Web Apps",
  title: "Technical SEO for SaaS platforms",
  description: "I solve rendering roadblocks, clean up faceted navigation bloat, and ensure client-side rendering doesn't hide your platform from search bots. You get code-level audit notes ready for your sprint tickets.",
  deliverables: ["JavaScript rendering and hydration audits", "Indexation and canonical tag architecture", "Core Web Vitals remediation for heavy web applications"],
  linkTo: "/TechnicalSeoPage",
  linkText: "Explore technical SEO",
  icon: CodeXml
}, {
  id: "content-strategy",
  tag: "High-Intent Acquisition",
  title: "Content strategy for feature & comparison pages",
  description: "I architect high-converting comparison hubs ('vs' and 'alternative to') and product feature pages that rank for commercial-intent keywords and drive qualified demo requests directly to your sales team.",
  deliverables: ["Competitor alternative and comparison page templates", "Feature-to-query semantic topic mapping", "Intent-focused content briefs written for conversion"],
  linkTo: "/B2BSeoLeadGenerationPage",
  linkText: "View B2B SEO details",
  icon: Layers
}, {
  id: "international-growth",
  tag: "Global Expansion",
  title: "Multi-market & international growth",
  description: "I design hreflang configurations and geo-targeting site architectures across subdirectories or ccTLDs to ensure you capture international search demand without cannibalising domestic performance.",
  deliverables: ["Hreflang validation and cross-domain XML sitemaps", "Regional search intent and keyword localisation", "Subfolder vs ccTLD domain structure advisory"],
  linkTo: "/InternationalSeoPage",
  linkText: "Review international SEO",
  icon: Globe2
}, {
  id: "ai-search-visibility",
  tag: "Modern Discovery",
  title: "AI search visibility & Answer Engine Optimisation",
  description: "I adapt your digital footprint so that modern LLM search engines and AI overviews cite your brand, understand your factual entities, and present your business as an authoritative primary source.",
  deliverables: ["Entity graph validation and custom JSON-LD schemas", "Citation source analysis across search LLMs", "Content structuring for conversational answer retrieval"],
  linkTo: "/AISearchOptimisationPage",
  linkText: "Learn about AI search",
  icon: Sparkles
}];
export default function Specialties() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultingspecialties" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mb-14 md:mb-18">
          <Badge variant="secondary" className="mb-4 font-mono uppercase tracking-wider text-xs font-semibold px-3 py-1 bg-secondary text-secondary-foreground">
            Direct Senior Guidance
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Consulting areas where I build measurable organic advantages
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            I do not delegate strategy or hand off client work to junior account managers. When you hire me, you work directly with me on these core strategic pillars.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {specialtiesData.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: i * 0.08
          }} className="h-full flex">
                <Card className="flex flex-col justify-between w-full bg-card border border-border shadow-sm hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                        <Icon className="w-6 h-6" />
                      </div>
                      <span className="text-xs font-mono font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                        {item.tag}
                      </span>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-default font-bold text-card-foreground leading-snug pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-6 flex-1">
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>

                    <div className="border-t border-border pt-4">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        What I personally implement:
                      </p>
                      <ul className="space-y-2.5">
                        {item.deliverables.map((deliverable, index) => <li key={index} data-index={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 leading-normal">
                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{deliverable}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border">
                    <Button asChild variant="link" className="px-0 font-medium text-primary hover:text-primary/80 group">
                      <Link to={item.linkTo} className="inline-flex items-center gap-1.5">
                        <span>{item.linkText}</span>
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-14 p-6 sm:p-8 rounded-xl bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-default font-bold text-foreground">
              Have an architecture or indexing challenge?
            </h3>
            <p className="text-sm text-muted-foreground">
              Share your URL and search hurdles. I review your setup directly before we speak.
            </p>
          </div>
          <Button asChild className="shrink-0 font-medium">
            <Link to="/ContactPage">Book a consultation with me</Link>
          </Button>
        </div>
      </div>
    </section>;
}